const SESSION_STORAGE_KEY_UTM = 'dh:utm-params'
const UTM_PREFIX = 'utm_'

export type UtmParams = Record<string, string>

const readFromSessionStorage = (): UtmParams => {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY_UTM)
    return raw ? (JSON.parse(raw) as UtmParams) : {}
  } catch (err) {
    console.warn('Failed to read UTM params from sessionStorage', err)
    return {}
  }
}

const writeToSessionStorage = (params: UtmParams): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY_UTM, JSON.stringify(params))
  } catch (err) {
    console.warn('Failed to persist UTM params to sessionStorage', err)
  }
}

const parseUtmFromUrl = (): UtmParams => {
  if (typeof window === 'undefined') return {}
  const searchParams = new URLSearchParams(window.location.search)
  const fromUrl: UtmParams = {}
  searchParams.forEach((value, key) => {
    if (key.startsWith(UTM_PREFIX) && value) {
      fromUrl[key] = value
    }
  })
  return fromUrl
}

/**
 * Reads `utm_*` params from the current URL and persists them to sessionStorage
 * so they survive OAuth/magic-link redirects. Should be called once at app startup.
 */
export const captureUtmParams = (): void => {
  const fromUrl = parseUtmFromUrl()
  if (Object.keys(fromUrl).length > 0) {
    writeToSessionStorage(fromUrl)
  }
}

/**
 * Returns the captured `utm_*` params for the current session. Falls back to
 * params present on the current URL if nothing was captured yet.
 */
export const getUtmParams = (): UtmParams => {
  const stored = readFromSessionStorage()
  if (Object.keys(stored).length > 0) return stored
  return parseUtmFromUrl()
}

/**
 * Removes any captured `utm_*` params from sessionStorage. Should be called on
 * logout so a subsequent sign-in is not attributed to a previous campaign.
 */
export const clearUtmParams = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY_UTM)
  } catch (err) {
    console.warn('Failed to clear UTM params from sessionStorage', err)
  }
}
